<template>
<div id="form-container" ref="formContainer">
    <div id="pgTitle" tabindex="-1"><h1>{{pageTitle}}</h1></div>

    <form-wizard :stepSize="stepSize"
                 :nextButtonText="buttons.next"
                 :backButtonText="buttons.back"
                 :finishButtonText="buttons.submit"
                 color="#003865"
                 :layout="layout"
                 @on-complete="onSubmit"
                 @on-change="switchTabs"
                 id="wizard">

        <span slot="title" tabindex="-1"/>

        <tab-content title="Personal" :before-change="()=>validateStep('personal')" :after-change="()=>homeAddressCheck()">
            <Personal ref="personal" @on-validate="mergePartialModels" :renewalApplication="renewalApplication"
                      :profile="profile"/>
        </tab-content>

        <tab-content title="Employment"
                     :before-change="()=>validateStep('emp')" :after-change="()=>scrollTo()">
            <Employment ref="emp" @on-validate="mergePartialModels" :renewalApplication="renewalApplication"/>
        </tab-content>

        <tab-content v-if="!isFull()" title="Questionnaire"
                     :before-change="()=>validateStep('ques')" :after-change="()=>scrollTo()">
            <Question ref="ques" @on-validate="mergePartialModels" :renewalApplication="renewalApplication"/>
        </tab-content>

        <tab-content v-if="!isFull()" title="Attachments"
                     :before-change="()=>validateStep('att')" :after-change="()=>scrollTo()">
            <Attachments ref="att" @on-validate="mergePartialModels" :app="renewalApplication" v-bind:current="renewalApplication.attachments"/>
        </tab-content>

        <tab-content v-if="isFull()"  title="Professional" :before-change="()=>validateStep('professional')" :after-change="()=>scrollTo()">
            <Professional ref="professional" @on-validate="mergePartialModels" :renewalApplication="renewalApplication"/>
        </tab-content>

        <tab-content v-if="isFull()" title="Other Licenses" :before-change="()=>validateStep('other')" :after-change="()=>scrollTo()">
            <OtherLicense ref="other" @on-validate="mergePartialModels" :renewalApplication="renewalApplication"/>
        </tab-content>

        <tab-content v-if="isFull()" title="Additional Information" :before-change="()=>validateStep('addl')" :after-change="()=>scrollTo()">
            <Additional ref="addl" @on-validate="mergePartialModels" :renewalApplication="renewalApplication"/>
        </tab-content>

        <tab-content v-if="!isFull()" title="Additional Information"
                     :before-change="()=>validateStep('addlInfo')" :after-change="()=>scrollTo()">
            <AddlInfo ref="addlInfo" @on-validate="mergePartialModels" :application="renewalApplication"/>
        </tab-content>
    </form-wizard>
</div>

</template>

<script>
import axios from "axios"
import { commonOptions } from '@/mixins/commonOptions.js'
import { buttons, breadcrumbs } from '@/mixins/constants.js'
import Start from '@/components/renewal-wizard/Start.vue'
import Personal from '@/components/renewal-wizard/Personal.vue'
import Employment from '@/components/renewal-wizard/Employment.vue'
import Question from '@/components/renewal-wizard/Question.vue'
import Professional from '@/components/renewal-wizard/Professional.vue'
import OtherLicense from '@/components/renewal-wizard/OtherLicense.vue'
import Additional from '@/components/renewal-wizard/Additional.vue'
import Attachments from '@/components/wizard/Attachments.vue'
import AddlInfo from '@/components/wizard/AddlInfo.vue'
import Confirm from '@/components/renewal-wizard/Confirm.vue'

import axiosRetry from 'axios-retry'

export default {
    components: {
        Personal,
        Professional,
        OtherLicense,
        Additional,
        Employment,
        Question,
        Attachments,
        AddlInfo,
    },
    mixins: [
        commonOptions,
        breadcrumbs,
        buttons
    ],
    data() {
        return {
            stepSize: 'sm',
            layout: '',
            isSubmitted: false,
            pageTitle: '',
            renewalApplication: {},
            isDisabled: false,
            profile: { }
        }
    },
    created() {
        document.title = 'Edit Technician Renewal Application'
        this.pageTitle = document.title
        this.loadProfile()
        if (this.$route.params.id) {
            this.loadRenewal(this.$route.params.id)
        }
    },
    methods: {
        isFull() {
            return this.renewalApplication && this.renewalApplication.licenseSubCategory === 'FULL'
        },
        loadRenewal(id) {
            axios.get(`${process.env.VUE_APP_API_BASE_URL}/application/renewal/${id}`)
                .then(response => {
                    this.renewalApplication = response.data
                    if (response.data.address !== null) {
                        for(var i = 0; i < response.data.address.length; i++) {
                            this.renewalApplication.address[i].addressType =
                                response.data.address[i]?.addressType?.code
                        }
                    }
                    this.renewalApplication.prevMnSupervisor = JSON.parse(JSON.stringify(this.renewalApplication.mnSupervisor))
                    if (this.renewalApplication.prevMnSupervisor !== null) {
                        this.renewalApplication.prevMnSupervisor.forEach(p => {
                            p.edited = true
                            p.establishment.establishmentHours.forEach((hrs) => {
                                if (hrs.days.some(d => d.code != undefined)) {
                                    hrs.days = hrs.days.map(d => d.code)
                                }
                            })
                        })
                    }
                })
                .catch(err => { console.log("ERROR: " + err) })
        },
        buttons: function() {
            return buttons
        },
        breadcrumbs: function() {
            return breadcrumbs
        },
        onSubmit() {
            if (!this.isDisabled) {
                this.isDisabled = true
                delete this.renewalApplication.licenseType
                delete this.renewalApplication.license
                if (this.renewalApplication.issuedOtherLicense !== 'Y') {
                    this.renewalApplication.otherLicenses = []
                }
                if (this.renewalApplication.adverseActionTaken !== 'Y') {
                    this.renewalApplication.adverseActionText = ''
                }
                if (this.renewalApplication.conviction !== 'Y') {
                    this.renewalApplication.convictionText = ''
                }
                if (this.renewalApplication.licenseSubCategory === 'FULL' ) {
                    this.renewalApplication.mnSupervisor = []
                } else {
                    this.renewalApplication.employers = []
                }
                this.sendToServer()
            }
        },
        sendToServer() {
            axiosRetry(axios, { retries: 0 })

            axios.post(`${process.env.VUE_APP_API_BASE_URL}/renewal/update/`, this.renewalApplication)
                .then(response => {
                    this.isSubmitted = true
                    this.$router.push('/home')
            }).catch(err => {
                    this.showRootToast("Error",
                        "The application has encountered an issue.  Please DO NOT resubmit, return to the home page to continue",
                        "danger")
                }
            )
        },

        validateStep(name) {
            var refToValidate = this.$refs[name]
            return refToValidate.validate()
        },
        mergePartialModels(model, isValid) {
            if(isValid){            
                this.renewalApplication = Object.assign({}, this.renewalApplication, model)
                if (this.renewalApplication.opts) {
                    delete this.renewalApplication.opts
                }
            }
        },
        mergeCheck(model) {
            this.isCheck = model['isCheck']
        },
        scrollTo() {
            let elem = document.getElementById("wizard")
            elem.scrollIntoView()
        },
        focus(e) {
            let elem = document.getElementsByClassName("card-header")[e+1]
            elem.focus()
        },
        switchTabs(prevIdx, nextIdx) {
            var title = 'Welcome'
            if (this.isFull()) {
            switch (nextIdx) {
                case 0:
                    title = 'Personal Information'
                    break;
                case 1:
                    title = 'Professional Information'
                    break;
                case 2:
                    title = 'Other Licenses'
                    break;
                case 3:
                    title = 'Additional Information'
                    break;
                default:
                    title = 'Application'
                }
            } else {
                switch (nextIdx) {
                    case 0:
                        title = 'Personal Information'
                        break;
                    case 1:
                        title = 'Professional Information'
                        break;
                    case 2:
                        title = 'Questionnaire'
                        break;
                    case 3:
                        title = 'Attachments'
                        break;
                    case 4:
                        title = 'Additional Information'
                        break;
                    default:
                        title = 'Application'
                }
            }
            document.title = title + ' - Technician Renewal Application - Body Art'
            this.pageTitle = document.title
            document.getElementById('pgTitle').focus()
        },
        removeTabFocus() {
            this.$nextTick(function () {
                var steps = document.querySelectorAll("div[role='tab']")
                steps.forEach(step => {
                    step.setAttribute("tabindex", "-1")
                })

                var steps2 = document.querySelectorAll(".wizard-nav-pills>li>a");
                steps2.forEach(step => {
                    step.setAttribute("tabindex", "-1")
                    step.setAttribute("aria-disabled","true")
                })
            })
        },
        homeAddressCheck() {
            if(this.renewalApplication.address[0].addressType == null) {
                this.renewalApplication.address[0].addressType = 'H';
                this.renewalApplication.address[0].preferred = true
            }
            scrollTo();
        },
        showRootToast(title, message, type) {
            this.$root.$bvToast.toast(message, {
                title: title,
                toaster: 'b-toaster-top-center',
                solid: true,
                variant: type,
                appendToast: true,
                noAutoHide:true
            })
            this.toastFocus()
        },
        toastFocus() {
            setTimeout(() => {
                let elem = document.getElementsByClassName("toast")
                elem = elem[elem.length-1]
                elem.focus()
            }, 100)
        },
        loadProfile() {
            axiosRetry(axios, {
                retries: 3,
                retryDelay: axiosRetry.exponentialDelay
            })

            axios.get(`${process.env.VUE_APP_API_BASE_URL}/profile`)
                .then(response => {
                    this.profile = response.data
                })
        },
    },
    updated: function() {
        this.removeTabFocus()
    },
    mounted() {
        this.removeTabFocus()
        this.scrollTo()
    },
}
</script>

<style>
.card {
    margin-bottom: 18px;
}
.card-header {
    font-weight: 700;
}
</style>